import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MarketingChrome } from "../components/MarketingChrome";
import { TERMS_VERSION } from "../components/ContributorTerms";
import { CountrySelect } from "../components/CountrySelect";
import { IconArrow, IconEye, IconEyeOff, IconInfo } from "../components/Icons";
import { auth as authApi } from "../api/endpoints";
import { useAuth } from "../store/auth";
import { useConfig } from "../store/config";
import type { ApiError } from "../api/http";
import { routeForContributor } from "../lib/routing";
import {
  validateEmail,
  validateName,
  validatePassword,
  validatePhone,
} from "../lib/validation";
import type { Gender } from "../types/api";

type InviteState = "idle" | "checking" | "valid" | "invalid";

const DIAL_CODES = ["+1", "+44", "+61", "+63", "+91", "+971"];

const GENDER_CHOICES: Array<{ id: string; label: string }> = [
  { id: "female", label: "Woman" },
  { id: "male", label: "Man" },
  { id: "non-binary", label: "Non-binary" },
  { id: "unspecified", label: "Prefer not to say" },
];

export function Signup() {
  const signup = useAuth((state) => state.signup);
  const loading = useAuth((state) => state.loading);
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dial, setDial] = useState("+1");
  const [national, setNational] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("United States");
  // Set by the invite when they were accepted for a particular country.
  const [lockedCountry, setLockedCountry] = useState<string | null>(null);
  const genders = useConfig((state) => state.config.genders);
  const config = useConfig((state) => state.config);
  const phone = national.trim() ? `${dial} ${national.trim()}` : "";
  const genderOptions = GENDER_CHOICES.map((item) => {
    const fromConfig = genders.find((option) => option.id === item.id);
    const known = item.id === "male" || item.id === "female";
    return {
      ...item,
      available: fromConfig ? fromConfig.available !== false : known,
      message: fromConfig?.message ?? "This option isn't open yet.",
    };
  });
  // Options the catalogue cannot serve yet are offered and explained, but
  // cannot be submitted — an account created against one would be handed
  // nothing to record.
  const chosenGender = genderOptions.find((option) => option.id === gender);
  const genderUnavailable = chosenGender?.available === false;
  const [searchParams] = useSearchParams();
  // An accepted applicant arrives from their email with the code in the
  // link, so it is filled in for them and checked straight away.
  const [invite, setInvite] = useState(() => (searchParams.get("invite") ?? "").trim());
  const [inviteState, setInviteState] = useState<InviteState>("idle");
  const [inviteReason, setInviteReason] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  // Field-level complaints from the API (an email already taken, a number
  // already registered) shown against the field rather than in a footnote.
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const errors: Record<string, string | null> = {
    name: serverErrors.name ?? validateName(name),
    email: serverErrors.email ?? validateEmail(email),
    phone: serverErrors.phone ?? validatePhone(phone),
    password: serverErrors.password ?? validatePassword(password),
    country: country ? null : "Choose the country you'll be recording from",
    gender: gender
      ? genderUnavailable
        ? "This option isn't open yet — see below"
        : null
      : "Select a gender so we can send you the right content",
  };

  // Check the invite as they type, so a bad code is caught before they have
  // filled in everything else.
  useEffect(() => {
    const code = invite.trim();
    // The empty case is handled in the change handler; this effect only ever
    // runs the debounced lookup, so it never sets state synchronously.
    if (!code) return;
    // An invite from an application only works for the address it was sent
    // to. Check against the email once it looks complete — not while it is
    // half-typed, which would flash "different address" at every keystroke.
    const emailToCheck = validateEmail(email) === null ? email.trim() : undefined;
    const timer = window.setTimeout(async () => {
      try {
        const result = await authApi.checkInvite(code, emailToCheck);
        setInviteState(result.valid ? "valid" : "invalid");
        setInviteReason(result.valid ? null : (result.reason ?? "That code isn't valid"));
        // Their rate was decided from the country they applied with, so the
        // form fills it in rather than letting them pick a different one and
        // be refused on submit.
        if (result.valid && result.country) {
          setLockedCountry(result.country);
          setCountry(result.country);
        } else {
          setLockedCountry(null);
        }
      } catch {
        // The API may not require invites at all; don't block signup on it.
        setInviteState("idle");
        setInviteReason(null);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [invite, email]);

  const hasErrors = Object.values(errors).some(Boolean) || !acceptedTerms;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched({
      name: true, email: true, phone: true, password: true, gender: true,
      country: true, terms: true,
    });
    setError(null);
    setServerErrors({});
    if (hasErrors) return;

    try {
      const contributor = await signup({
        name: name.trim(),
        email: email.trim(),
        password,
        phone: phone.trim(),
        gender: gender as Gender,
        country,
        accepted_terms: acceptedTerms,
        terms_version: TERMS_VERSION,
        invite_code: invite.trim() || undefined,
      });
      navigate(routeForContributor(contributor), { replace: true });
    } catch (err) {
      const apiError = err as ApiError;

      // Point the person at the field that is actually the problem.
      const mapped: Record<string, string> = {};
      if (apiError.code === "email_taken") {
        mapped.email = "An account already exists for this email. Try signing in instead.";
      }
      if (apiError.code === "invite_email_mismatch") {
        mapped.email = apiError.message;
      }
      if (apiError.code === "phone_taken") {
        mapped.phone =
          "An account already exists for this WhatsApp number. Try signing in instead.";
      }
      if (Array.isArray(apiError.details)) {
        for (const entry of apiError.details as Array<{ field?: string; message?: string }>) {
          const field = entry.field?.split(".").pop();
          if (field && entry.message && field in errors) mapped[field] = entry.message;
        }
      }

      setServerErrors(mapped);
      // Only show the banner when nothing landed on a field, so the same
      // complaint is never shown twice.
      setError(Object.keys(mapped).length > 0 ? null : apiError.message);
    }
  }

  function fieldClass(key: string): string {
    return touched[key] && errors[key] ? "field err" : "field";
  }

  return (
    <MarketingChrome back={{ to: "/", label: "Back to Inputry", short: "Back" }} footer="compact">
      <section className="auth-page signup-page">
        <div className="auth-split">
          <div className="hero-grid">
            <div>
              <form className="panel-card signup-form" onSubmit={handleSubmit} noValidate>
                <h2>Create your contributor account</h2>

                <div className={fieldClass("name")}>
                  <label htmlFor="name">Full name</label>
                  <input
                    id="name"
                    value={name}
                    placeholder="Your Full Name"
                    autoComplete="name"
                    maxLength={20}
                    onChange={(event) => {
                      setName(event.target.value);
                      setServerErrors((previous) => ({ ...previous, name: "" }));
                    }}
                    onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  />
                  <div className="err-msg">{errors.name}</div>
                </div>

                <div className={email.trim() && !errors.email ? "field valid" : fieldClass("email")}>
                  <label htmlFor="email">Email</label>
                  <div className="input-mark">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      placeholder="Enter email"
                      autoComplete="email"
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setServerErrors((previous) => ({ ...previous, email: "" }));
                      }}
                      onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    />
                    {email.trim() && !errors.email && <i className="mark" aria-hidden="true" />}
                  </div>
                  <div className="hint">Use the email that received your invitation.</div>
                  <div className="err-msg">{touched.email ? errors.email : ""}</div>
                </div>

                <div className={fieldClass("phone")}>
                  <label htmlFor="phone">WhatsApp number</label>
                  <div className="phone-row">
                    <select
                      aria-label="Country code"
                      value={dial}
                      onChange={(event) => setDial(event.target.value)}
                    >
                      {DIAL_CODES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                    <input
                      id="phone"
                      type="tel"
                      value={national}
                      placeholder="(555) 000-0000"
                      autoComplete="tel-national"
                      inputMode="tel"
                      maxLength={16}
                      onChange={(event) => {
                        setNational(event.target.value);
                        setServerErrors((previous) => ({ ...previous, phone: "" }));
                      }}
                      onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                    />
                  </div>
                  <div className="err-msg">{errors.phone}</div>
                </div>

                <div className={fieldClass("password")}>
                  <label htmlFor="password">Password</label>
                  <div className="password-wrap">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      onChange={(event) => {
                        setPassword(event.target.value);
                        setServerErrors((previous) => ({ ...previous, password: "" }));
                      }}
                      onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((open) => !open)}
                    >
                      {showPassword ? <IconEyeOff width={18} height={18} /> : <IconEye width={18} height={18} />}
                    </button>
                  </div>
                  <div className="err-msg">{errors.password}</div>
                </div>

                <div className={fieldClass("gender")}>
                  <span className="field-label">Gender</span>
                  <div className="choice-row" role="radiogroup" aria-label="Gender">
                    {genderOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={gender === option.id ? "choice on" : "choice"}
                        aria-pressed={gender === option.id}
                        onClick={() => {
                          setGender(option.id);
                          setTouched((t) => ({ ...t, gender: true }));
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {genderUnavailable && <div className="notice">{chosenGender?.message}</div>}
                  <div className="err-msg">{genderUnavailable ? "" : errors.gender}</div>
                </div>

                <div className={country && !errors.country ? "field valid" : fieldClass("country")}>
                  <label htmlFor="country">Where you'll be recording from</label>
                  {lockedCountry ? (
                    <input id="country" value={countryName(config, lockedCountry)} disabled />
                  ) : (
                    <CountrySelect value={country} onChange={setCountry} />
                  )}
                  <div className="err-msg">{touched.country ? errors.country : ""}</div>
                  {country && (
                    <>
                      <div className="careful">
                        <IconInfo width={16} height={16} />
                        <div>
                          <strong>Choose carefully</strong>
                          <p>Your country sets your rate and can't be changed later.</p>
                        </div>
                      </div>
                      <p className="your-rate">Your rate: $0.05 per approved voice note</p>
                    </>
                  )}
                </div>

                <div
                  className={
                    inviteState === "invalid"
                      ? "field err"
                      : inviteState === "valid"
                        ? "field valid"
                        : "field"
                  }
                >
                  <label htmlFor="invite">Invite code</label>
                  <div className="input-mark">
                    <input
                      id="invite"
                      value={invite}
                      placeholder="Paste your code"
                      autoComplete="off"
                      maxLength={20}
                      onChange={(event) => {
                        const next = event.target.value.toUpperCase();
                        setInvite(next);
                        setInviteState(next.trim() ? "checking" : "idle");
                        setInviteReason(null);
                      }}
                    />
                    {inviteState === "valid" && <i className="mark" aria-hidden="true" />}
                  </div>
                  {inviteState === "checking" && <div className="hint">Checking code…</div>}
                  <div className="err-msg">{inviteReason}</div>
                </div>

                <div className="before-join">
                  <strong>Before you join</strong>
                  <p>Payment · Content rules · Voice usage rights</p>
                  <Link to="/terms">Read Contributor Terms <IconArrow width={14} height={14} /></Link>
                </div>

                <div className="terms-accept">
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(event) => setAcceptedTerms(event.target.checked)}
                    />
                    <span className="check-box" />
                    <span>
                      I have read and agree to the Inputry{" "}
                      <Link to="/terms">Contributor Terms &amp; Conditions</Link> and{" "}
                      {/* <Link to="/privacy">Privacy Policy</Link>. */}
                    </span>
                  </label>
                  {touched.terms && !acceptedTerms && (
                    <div className="form-error">
                      You need to accept the terms before you can join.
                    </div>
                  )}
                </div>

                {error && <div className="form-error">{error}</div>}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || genderUnavailable}
                >
                  {loading ? "Creating your account…" : "Create contributor account"}
                </button>
              </form>
            </div>

            <aside className="invite-aside">
              <div className="kicker"><i /> Contributor network</div>
              <h1 className="invite-title">
                You're <span>invited.</span>
              </h1>
              <div className="rate-hero">
                <small>CONTRIBUTOR RATE</small>
                <div className="amt">$3</div>
                <div className="rate-note">Hourly Earnings</div>
                <div className="pills">
                  <span>PayPal/Wise</span>
                  {country && <span>{countryName(config, country)}</span>}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </MarketingChrome>
  );
}

/** The country's name, for the locked field — the code alone means nothing. */
function countryName(config: { countries: Array<{ code: string; name: string }> }, code: string) {
  return config.countries.find((entry) => entry.code === code)?.name ?? code;
}
